import {assert} from 'Asserts';
import {ActionOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';

import {processTimingEvent} from './BenchlingProfiler';
import {toDatadogTagFormat} from './PerformanceUtils';
import {ProfilerEvent, ProfilerEventDomain, EventMetadata} from './ProfilerConstants';

const PROFILER_SAMPLE_TESTS_EVENTS_VERSION = 2;

export enum ProfilerSampleTestsFeatures {
  TEST_DEFINITION_CREATE = 'testDefinitionCreate',
  TEST_DEFINITION_UPDATE = 'testDefinitionUpdate',
  TEST_DEFINITION_ARCHIVE_UNARCHIVE = 'testDefinitionArchiveUnarchive',
  TEST_ORDER_BULK_CREATE = 'testOrderBulkCreate',
  TEST_ORDER_BULK_UPDATE = 'testOrderBulkUpdate',
}
const FEATURE_TO_PROCESSING_FUNCTION: {[key in ProfilerSampleTestsFeatures]: Function} = {
  [ProfilerSampleTestsFeatures.TEST_DEFINITION_CREATE]: processTestDefinitionCreate,
  [ProfilerSampleTestsFeatures.TEST_DEFINITION_UPDATE]: processTestDefinitionUpdate,
  [ProfilerSampleTestsFeatures.TEST_DEFINITION_ARCHIVE_UNARCHIVE]: processTestDefinitionArchiveUnarchive,
  [ProfilerSampleTestsFeatures.TEST_ORDER_BULK_CREATE]: processTestOrderBulkCreate,
  [ProfilerSampleTestsFeatures.TEST_ORDER_BULK_UPDATE]: processTestOrderBulkUpdate,
};
enum ProfilerSampleTestActions {
  TEST_DEFINITION_CREATE_STARTED = 'testDefinitionCreateStarted',
  TEST_DEFINITION_CREATE_ENDED = 'testDefinitionCreateEnded',
  TEST_DEFINITION_UPDATE_STARTED = 'testDefinitionUpdateStarted',
  TEST_DEFINITION_UPDATE_ENDED = 'testDefinitionUpdateEnded',
  TEST_DEFINITION_ARCHIVE_UNARCHIVE_STARTED = 'testDefinitionArchiveUnarchiveStarted',
  TEST_DEFINITION_ARCHIVE_UNARCHIVE_ENDED = 'testDefinitionArchiveUnarchiveEnded',
  TEST_ORDER_BULK_CREATE_STARTED = 'testOrderBulkCreateStarted',
  TEST_ORDER_BULK_CREATE_ENDED = 'testOrderBulkCreateEnded',
  TEST_ORDER_BULK_UPDATE_STARTED = 'testOrderBulkUpdateStarted',
  TEST_ORDER_BULK_UPDATE_ENDED = 'testOrderBulkUpdateEnded',
}

export const TEST_DEFINITION_CREATE_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.SAMPLE_TESTS,
  feature: ProfilerSampleTestsFeatures.TEST_DEFINITION_CREATE,
  action: ProfilerSampleTestActions.TEST_DEFINITION_CREATE_STARTED,
};
export const TEST_DEFINITION_CREATE_ENDED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.SAMPLE_TESTS,
  feature: ProfilerSampleTestsFeatures.TEST_DEFINITION_CREATE,
  action: ProfilerSampleTestActions.TEST_DEFINITION_CREATE_ENDED,
};
export const TEST_DEFINITION_UPDATE_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.SAMPLE_TESTS,
  feature: ProfilerSampleTestsFeatures.TEST_DEFINITION_UPDATE,
  action: ProfilerSampleTestActions.TEST_DEFINITION_UPDATE_STARTED,
};
export const TEST_DEFINITION_UPDATE_ENDED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.SAMPLE_TESTS,
  feature: ProfilerSampleTestsFeatures.TEST_DEFINITION_UPDATE,
  action: ProfilerSampleTestActions.TEST_DEFINITION_UPDATE_ENDED,
};
export const TEST_DEFINITION_ARCHIVE_UNARCHIVE_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.SAMPLE_TESTS,
  feature: ProfilerSampleTestsFeatures.TEST_DEFINITION_ARCHIVE_UNARCHIVE,
  action: ProfilerSampleTestActions.TEST_DEFINITION_ARCHIVE_UNARCHIVE_STARTED,
};
export const TEST_DEFINITION_ARCHIVE_UNARCHIVE_ENDED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.SAMPLE_TESTS,
  feature: ProfilerSampleTestsFeatures.TEST_DEFINITION_ARCHIVE_UNARCHIVE,
  action: ProfilerSampleTestActions.TEST_DEFINITION_ARCHIVE_UNARCHIVE_ENDED,
};

export const TEST_ORDER_BULK_CREATE_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.SAMPLE_TESTS,
  feature: ProfilerSampleTestsFeatures.TEST_ORDER_BULK_CREATE,
  action: ProfilerSampleTestActions.TEST_ORDER_BULK_CREATE_STARTED,
};

export const TEST_ORDER_BULK_CREATE_ENDED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.SAMPLE_TESTS,
  feature: ProfilerSampleTestsFeatures.TEST_ORDER_BULK_CREATE,
  action: ProfilerSampleTestActions.TEST_ORDER_BULK_CREATE_ENDED,
};

export const TEST_ORDER_BULK_UPDATE_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.SAMPLE_TESTS,
  feature: ProfilerSampleTestsFeatures.TEST_ORDER_BULK_UPDATE,
  action: ProfilerSampleTestActions.TEST_ORDER_BULK_UPDATE_STARTED,
};

export const TEST_ORDER_BULK_UPDATE_ENDED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.SAMPLE_TESTS,
  feature: ProfilerSampleTestsFeatures.TEST_ORDER_BULK_UPDATE,
  action: ProfilerSampleTestActions.TEST_ORDER_BULK_UPDATE_ENDED,
};

export function composeTestDefinitionArchiveUnarchiveEventMetadata(isArchiving: boolean): EventMetadata {
  const archiveTypeValue = isArchiving ? 'archive' : 'unarchive';
  return {
    distributionMetadata: {
      archive_type: archiveTypeValue,
    },
    actionMetadata: {
      archive_type: archiveTypeValue,
    },
  };
}

export function composeTestOrderBulkEventEventMetadata(
  numberOfTestOrders: number,
  actionOrigin: ActionOrigin
): EventMetadata {
  return {
    distributionMetadata: {
      action_origin: toDatadogTagFormat(actionOrigin),
    },
    actionMetadata: {
      number_of_test_orders: numberOfTestOrders,
      action_origin: toDatadogTagFormat(actionOrigin),
    },
  };
}

export function processSampleTestsEvents(profilerEvent: Pick<ProfilerEvent, 'feature' | 'action'>): void {
  const feature = profilerEvent.feature as ProfilerSampleTestsFeatures;
  assert(
    Object.values(ProfilerSampleTestsFeatures).includes(feature),
    `Unexpected profiler feature: ${profilerEvent.feature}`
  );
  FEATURE_TO_PROCESSING_FUNCTION[feature]();
}

function processTestDefinitionCreate(metadata: EventMetadata): void {
  processTimingEvent(
    TEST_DEFINITION_CREATE_STARTED_EVENT,
    TEST_DEFINITION_CREATE_ENDED_EVENT,
    'Test Definition Created',
    'perf.sample_tests.test_definition_created',
    PROFILER_SAMPLE_TESTS_EVENTS_VERSION
  );
}

function processTestDefinitionUpdate(metadata: EventMetadata): void {
  processTimingEvent(
    TEST_DEFINITION_UPDATE_STARTED_EVENT,
    TEST_DEFINITION_UPDATE_ENDED_EVENT,
    'Test Definition Updated',
    'perf.sample_tests.test_definition_updated',
    PROFILER_SAMPLE_TESTS_EVENTS_VERSION
  );
}

function processTestDefinitionArchiveUnarchive(metadata: EventMetadata): void {
  processTimingEvent(
    TEST_DEFINITION_ARCHIVE_UNARCHIVE_STARTED_EVENT,
    TEST_DEFINITION_ARCHIVE_UNARCHIVE_ENDED_EVENT,
    'Test Definition Archived/Unarchived',
    'perf.sample_tests.test_definition_archived_unarchived',
    PROFILER_SAMPLE_TESTS_EVENTS_VERSION
  );
}

function processTestOrderBulkCreate(metadata: EventMetadata): void {
  processTimingEvent(
    TEST_ORDER_BULK_CREATE_STARTED_EVENT,
    TEST_ORDER_BULK_CREATE_ENDED_EVENT,
    'Test Orders Bulk Created',
    'perf.sample_tests.test_orders_bulk_created',
    PROFILER_SAMPLE_TESTS_EVENTS_VERSION
  );
}

function processTestOrderBulkUpdate(metadata: EventMetadata): void {
  processTimingEvent(
    TEST_ORDER_BULK_UPDATE_STARTED_EVENT,
    TEST_ORDER_BULK_UPDATE_ENDED_EVENT,
    'Test Orders Bulk Updated',
    'perf.sample_tests.test_orders_bulk_updated',
    PROFILER_SAMPLE_TESTS_EVENTS_VERSION
  );
}
